import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";
import {
  Users,
  Mail,
  Phone,
  GraduationCap,
  IndianRupee,
  LogOut,
  MessageSquare,
  CheckCircle,
  Clock,
  AlertCircle,
  Eye,
  Sparkles,
  Briefcase,
  Download
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [adminExists, setAdminExists] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const [admin, setAdmin] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState("");

  // Auth form state
  const [authForm, setAuthForm] = useState({
    email: "",
    password: "",
    name: ""
  });

  // Check if admin exists
  useEffect(() => {
    checkAdminExists();
  }, []);

  // Verify token on mount
  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Load data when logged in
  useEffect(() => {
    if (isLoggedIn) {
      loadStats();
      loadContacts();
      loadEnrollments();
      loadJobs();
    }
  }, [isLoggedIn]);

  const checkAdminExists = async () => {
    try {
      const res = await axios.get(`${API}/admin/check`);
      setAdminExists(res.data.admin_exists);
    } catch (err) {
      console.error("Error checking admin:", err);
    }
  };

  const verifyToken = async () => {
    try {
      const res = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmin(res.data);
      setIsLoggedIn(true);
    } catch (err) {
      localStorage.removeItem("admin_token");
      setToken(null);
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, {
        email: authForm.email,
        password: authForm.password
      });
      localStorage.setItem("admin_token", res.data.token);
      setToken(res.data.token);
      setAdmin(res.data.admin);
      setIsLoggedIn(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(`${API}/admin/setup`, {
        email: authForm.email,
        password: authForm.password,
        name: authForm.name
      });
      localStorage.setItem("admin_token", res.data.token);
      setToken(res.data.token);
      setAdmin(res.data.admin);
      setIsLoggedIn(true);
      setAdminExists(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Setup failed");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
    setIsLoggedIn(false);
    setAdmin(null);
  };

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error("Error loading stats:", err);
    }
  };

  const loadContacts = async () => {
    try {
      const res = await axios.get(`${API}/admin/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(res.data.contacts);
    } catch (err) {
      console.error("Error loading contacts:", err);
    }
  };

  const loadEnrollments = async () => {
    try {
      const res = await axios.get(`${API}/admin/enrollments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnrollments(res.data.enrollments);
    } catch (err) {
      console.error("Error loading enrollments:", err);
    }
  };

  const loadJobs = async () => {
    try {
      const res = await axios.get(`${API}/admin/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(res.data.jobs);
    } catch (err) {
      console.error("Error loading jobs:", err);
    }
  };

  const exportCSV = (rows, columns, filename) => {
    if (!rows || rows.length === 0) {
      alert("No data to export.");
      return;
    }
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/"/g, '""');
      return `"${s}"`;
    };
    const header = columns.map((c) => escape(c.label)).join(",");
    const body = rows
      .map((row) => columns.map((c) => escape(row[c.key])).join(","))
      .join("\n");
    const csv = `${header}\n${body}`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `${filename}-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportContacts = () =>
    exportCSV(
      contacts,
      [
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "message", label: "Message" },
        { key: "status", label: "Status" },
        { key: "created_at", label: "Submitted At" },
      ],
      "contacts"
    );

  const exportJobs = () =>
    exportCSV(
      jobs,
      [
        { key: "company_name", label: "Company" },
        { key: "contact_person", label: "Contact Person" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "job_title", label: "Role" },
        { key: "location", label: "Location" },
        { key: "experience", label: "Experience" },
        { key: "salary_range", label: "CTC" },
        { key: "openings", label: "Openings" },
        { key: "job_description", label: "Description" },
        { key: "created_at", label: "Posted At" },
      ],
      "jobs"
    );

  const updateContactStatus = async (contactId, status) => {
    try {
      await axios.patch(
        `${API}/admin/contacts/${contactId}?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadContacts();
      loadStats();
    } catch (err) {
      console.error("Error updating contact:", err);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short"
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN").format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Auth Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Sparkles className="h-8 w-8 text-orange-500" />
              <span className="text-2xl font-bold text-orange-500">MC Talent Solutions</span>
            </div>
            <CardTitle className="text-xl">
              {adminExists === false ? "Admin Setup" : "Admin Login"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={adminExists === false ? handleSetup : handleLogin} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </div>
              )}

              {adminExists === false && (
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    placeholder="Enter your name"
                    value={authForm.name}
                    onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })}
                    required
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="hiring@mctalentsolutionsai.com"
                  value={authForm.email}
                  onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter password"
                  value={authForm.password}
                  onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                  required
                />
              </div>

              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600">
                {adminExists === false ? "Create Admin Account" : "Login"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-orange-500" />
            <span className="text-xl font-bold text-gray-900">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-gray-600">Welcome, {admin?.name}</span>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-8">
          {["dashboard", "contacts", "jobs", "enrollments"].map((tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? "default" : "outline"}
              onClick={() => setActiveTab(tab)}
              className={activeTab === tab ? "bg-orange-500 hover:bg-orange-600" : ""}
              data-testid={`admin-tab-${tab}`}
            >
              {tab === "dashboard" && <Eye className="h-4 w-4 mr-2" />}
              {tab === "contacts" && <MessageSquare className="h-4 w-4 mr-2" />}
              {tab === "jobs" && <Briefcase className="h-4 w-4 mr-2" />}
              {tab === "enrollments" && <GraduationCap className="h-4 w-4 mr-2" />}
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </Button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && stats && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-full">
                    <MessageSquare className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Inquiries</p>
                    <p className="text-2xl font-bold">{stats.contacts.total}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-orange-100 rounded-full">
                    <AlertCircle className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">New Inquiries</p>
                    <p className="text-2xl font-bold">{stats.contacts.new}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-full">
                    <GraduationCap className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Enrollments</p>
                    <p className="text-2xl font-bold">{stats.enrollments.total}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-full">
                    <IndianRupee className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Revenue</p>
                    <p className="text-2xl font-bold">₹{formatPrice(stats.revenue)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === "contacts" && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Contact Inquiries ({contacts.length})
                </CardTitle>
                <Button
                  onClick={exportContacts}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="export-contacts-csv"
                  disabled={contacts.length === 0}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {contacts.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No inquiries yet</p>
                ) : (
                  contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className={`p-4 rounded-lg border ${
                        contact.status === "new" ? "bg-orange-50 border-orange-200" : "bg-gray-50"
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold">
                            {contact.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <h4 className="font-semibold">{contact.name}</h4>
                            <div className="flex items-center gap-3 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {contact.email}
                              </span>
                              {contact.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {contact.phone}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {contact.status === "new" ? (
                            <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              New
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" />
                              {contact.status}
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-gray-700 mb-3">{contact.message}</p>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-400">{formatDate(contact.created_at)}</span>
                        <div className="flex gap-2">
                          {contact.status === "new" && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateContactStatus(contact.id, "contacted")}
                              >
                                Mark Contacted
                              </Button>
                              <Button
                                size="sm"
                                className="bg-green-500 hover:bg-green-600"
                                onClick={() => updateContactStatus(contact.id, "resolved")}
                              >
                                Resolve
                              </Button>
                            </>
                          )}
                          <a
                            href={`mailto:${contact.email}`}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-orange-500 text-white text-sm rounded-md hover:bg-orange-600"
                          >
                            <Mail className="h-3 w-3" />
                            Reply
                          </a>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Jobs Tab */}
        {activeTab === "jobs" && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Job Postings ({jobs.length})
                </CardTitle>
                <Button
                  onClick={exportJobs}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="export-jobs-csv"
                  disabled={jobs.length === 0}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {jobs.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No job postings yet</p>
                ) : (
                  jobs.map((job) => (
                    <div
                      key={job.id}
                      className="p-4 rounded-lg border bg-gray-50"
                      data-testid={`admin-job-${job.id}`}
                    >
                      <div className="flex justify-between items-start mb-2 flex-wrap gap-2">
                        <div>
                          <h4 className="font-semibold text-gray-900">{job.job_title}</h4>
                          <p className="text-sm text-orange-600 font-medium">{job.company_name}</p>
                        </div>
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                          {job.status || "active"}
                        </span>
                      </div>
                      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-600 mb-3">
                        {job.location && <div><span className="font-medium">Location:</span> {job.location}</div>}
                        {job.experience && <div><span className="font-medium">Experience:</span> {job.experience}</div>}
                        {job.salary_range && <div><span className="font-medium">CTC:</span> {job.salary_range}</div>}
                        {job.openings && <div><span className="font-medium">Openings:</span> {job.openings}</div>}
                      </div>
                      {job.contact_person && (
                        <div className="text-xs text-gray-500 mb-2">
                          Contact: {job.contact_person} · {job.email} · {job.phone}
                        </div>
                      )}
                      <p className="text-gray-700 text-sm whitespace-pre-line">{job.job_description}</p>
                      <div className="text-xs text-gray-400 mt-2">{formatDate(job.created_at)}</div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Enrollments Tab */}
        {activeTab === "enrollments" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                Course Enrollments ({enrollments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Student</th>
                      <th className="text-left py-3 px-4">Course</th>
                      <th className="text-left py-3 px-4">Amount</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-left py-3 px-4">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrollments.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-8 text-gray-500">
                          No enrollments yet
                        </td>
                      </tr>
                    ) : (
                      enrollments.map((enrollment) => (
                        <tr key={enrollment.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium">{enrollment.name}</p>
                              <p className="text-sm text-gray-500">{enrollment.email}</p>
                              <p className="text-sm text-gray-500">{enrollment.phone}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4">{enrollment.course_title}</td>
                          <td className="py-3 px-4">₹{formatPrice(enrollment.amount)}</td>
                          <td className="py-3 px-4">
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${
                                enrollment.status === "paid"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-yellow-100 text-yellow-700"
                              }`}
                            >
                              {enrollment.status.toUpperCase()}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500">
                            {formatDate(enrollment.created_at)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
